import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// project imports
import useSession from 'hooks/useSession';

// material-ui
import {
  Typography,
  Button,
  Container,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import Box from '@mui/material/Box';

// ==============================|| NOT FOUND - 404 PAGE ||============================== //

export default function NotFound() {
  const { isLoggedIn, user } = useSession();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Countdown timer for automatic redirect - only for non-logged-in users
    if (!isLoggedIn) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            navigate('/auth/login', { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isLoggedIn, navigate]);

  const handleManualRedirect = () => {
    if (isLoggedIn) {
      navigate('/', { replace: true });
    } else {
      navigate('/auth/login', { replace: true });
    }
  };

  // Content for logged-in users
  const LoggedInContent = () => (
    <>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Page Not Found
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Hi {user?.first_name || user?.username || 'there'}! The page you're looking for doesn't exist or has been moved.
      </Typography>

      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/', { replace: true })}
          size="large"
        >
          Go to Dashboard
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />
      
      <Typography variant="body2" color="text.secondary">
        Need help? Check your navigation menu or contact your system administrator.
      </Typography>
    </>
  );

  // Content for non-logged-in users
  const NotLoggedInContent = () => (
    <>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Page Not Found
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Sorry, the page you're looking for doesn't exist or requires authentication.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Redirecting you to login in {countdown} seconds...
      </Alert>

      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/auth/login', { replace: true })}
          size="large"
        >
          Login to Continue
        </Button>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/auth/forgot-password', { replace: true })}
          size="large"
        >
          Forgot Password?
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />
      
      <Typography variant="body2" color="text.secondary">
        If you believe you should have access to this page, please log in with your credentials.
      </Typography>
    </>
  );

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          py: 4
        }}
      >
        <Paper elevation={3} sx={{ p: 6, borderRadius: 2 }}>
          {/* 404 Icon/Number */}
          <Typography variant="h1" color="primary" gutterBottom sx={{ fontSize: '6rem', fontWeight: 'bold' }}>
            404
          </Typography>
          
          {/* Dynamic content based on authentication status */}
          {isLoggedIn ? <LoggedInContent /> : <NotLoggedInContent />}
        </Paper>
      </Box>
    </Container>
  );
}
